<template>
  <div class="search-loading-panel">
    <!-- 顶部搜索栏 -->
    <WorkbenchHeader
        :keyword="keyword"
        :ISshow="false"
        @back="emit('back')"
    />
    <div class="step-content">
        <!-- 步骤进度标签 -->
    <div class="step-wrap">
      <div 
        v-for="(step, index) in stepList" 
        :key="index"
        class="step-item"
        :class="{ active: currentStep >= index }"
      >
        <span class="step-dot"></span>
        <span class="step-text">{{ step.label }}</span>
      </div>
    </div>

    <!-- 骨架加载区域：无思考内容时显示 -->
    <div v-if="!rawThinkText" class="skeleton-card">
      <div class="skeleton-line line-short"></div>
      <div class="skeleton-line line-long1"></div>
      <div class="skeleton-line line-long2"></div>
      <div class="skeleton-line line-short" style="margin-top: 24px;"></div>
      <div class="skeleton-block"></div>
      <div class="skeleton-block"></div>
      <div class="skeleton-block"></div>
    </div>

    <!-- 深度思考面板：固定高度，可滚动 -->
    <div v-else class="think-panel">
      <div class="think-header">
        <div class="think-left">
          <span class="think-title">
            {{ thinkStreaming ? '深度思考中' : '深度思考完成' }}
            <span v-if="thinkStreaming" class="dot-anim">...</span>
          </span>
        </div>
      </div>
      <div class="think-scroll">
        <div v-html="thinkHtml"></div>
      </div>
    </div>
    </div>
    
  </div>
</template>

<script setup>
import WorkbenchHeader from "./WorkbenchHeader.vue"
import { ref, onMounted, onBeforeUnmount } from "vue"
import { marked } from 'marked'
import hljs from 'highlight.js'
import 'highlight.js/styles/atom-one-light.css'
import { getApiBaseUrl } from '@/utils/env'

const emit = defineEmits(["finish", "back"])
// 关键：用变量接收 defineProps 返回值
const props = defineProps({
  keyword: {
    type: String,
    default: ""
  }
})
// 消除eslint未使用props警告
void props

// 步骤
const stepList = ref([
  { label: "正在理解问题与案件事实" },
  { label: "正在检索案例与法律依据" },
  { label: "正在生成分析报告" }
])
const currentStep = ref(0)

// 思考面板状态
const thinkStreaming = ref(true)
const rawThinkText = ref('')
const thinkHtml = ref('')

// marked 配置
marked.setOptions({
  gfm: true,
  breaks: true,
  highlight(code, lang) {
    const langLabel = lang || 'text'
    let codeHtml = lang && hljs.getLanguage(lang)
      ? hljs.highlight(code, { language: lang, ignoreIllegals: true }).value
      : hljs.highlightAuto(code).value
    return `<div class="code-wrap">
      <span class="code-lang">${langLabel}</span>
      <button class="code-copy-btn">复制代码</button>
      <pre class="hljs"><code>${codeHtml}</code></pre>
    </div>`
  }
})

/**
 * 检测行是否包含表格单元格（排除代码块）
 */
const isTableLine = (line) => {
  const trimmed = line.trim()
  if (!trimmed) return false
  if (trimmed.startsWith('```') || trimmed.startsWith('~~~')) return false
  return trimmed.includes('|')
}

/**
 * 检测行是否是表格分隔线
 */
const isSeparatorLine = (line) => {
  const trimmed = line.trim()
  if (!trimmed) return false
  let content = trimmed
  if (content.startsWith('|')) content = content.slice(1)
  if (content.endsWith('|')) content = content.slice(0, -1)
  const matches = content.match(/:?-{3,}:?/g)
  return matches !== null && matches.length >= 1
}

/**
 * 解析表格行
 */
const parseTableRow = (line) => {
  const trimmed = line.trim()
  if (!trimmed) return { cells: [], count: 0 }
  let content = trimmed
  const startsWithPipe = content.startsWith('|')
  const endsWithPipe = content.endsWith('|')
  if (startsWithPipe) content = content.slice(1)
  if (endsWithPipe) content = content.slice(0, -1)
  const cells = content.split('|').map(c => c.trim())
  return { cells, count: cells.length, startsWithPipe, endsWithPipe }
}

/**
 * 修复表格行格式
 */
const fixTableRowLine = (cells, colCount) => {
  while (cells.length < colCount) cells.push('')
  if (cells.length > colCount) cells = cells.slice(0, colCount)
  return '| ' + cells.join(' | ') + ' |'
}

/**
 * 流式 Markdown 表格修复：处理分隔线缺 |、列数不齐、缺分隔线、未闭合等问题
 */
const fixStreamMarkdown = (text) => {
  if (!text) return ''
  const lines = text.split('\n')
  const result = []
  let i = 0
  let inCodeBlock = false

  while (i < lines.length) {
    const line = lines[i]
    const trimmed = line.trim()

    if (trimmed.startsWith('```') || trimmed.startsWith('~~~')) {
      inCodeBlock = !inCodeBlock
      result.push(line)
      i++
      continue
    }

    if (inCodeBlock) {
      result.push(line)
      i++
      continue
    }

    if (isTableLine(line)) {
      if (result.length > 0 && result[result.length - 1].trim() !== '') {
        result.push('')
      }

      const tableLines = []
      let j = i
      while (j < lines.length) {
        const tl = lines[j]
        const tTrimmed = tl.trim()
        if (tTrimmed.startsWith('```') || tTrimmed.startsWith('~~~')) break
        if (isTableLine(tl)) {
          tableLines.push(tl)
          j++
        } else if (tTrimmed === '') {
          // let hasMore = false
          let k = j + 1
          while (k < lines.length && lines[k].trim() === '') k++
          if (k < lines.length && isTableLine(lines[k])) {
            tableLines.push(tl)
            j++
          } else break
        } else break
      }

      const cleanLines = tableLines.filter(l => l.trim() !== '')
      if (cleanLines.length > 0) {
        let colCount = 0
        cleanLines.forEach(cl => {
          const parsed = parseTableRow(cl)
          colCount = Math.max(colCount, parsed.count)
          if (isSeparatorLine(cl)) {
            const matches = cl.match(/:?-{3,}:?/g)
            if (matches) colCount = Math.max(colCount, matches.length)
          }
        })
        colCount = Math.max(colCount, 1)

        let hasSeparator = false
        cleanLines.forEach(cl => { if (isSeparatorLine(cl)) hasSeparator = true })

        const headerParsed = parseTableRow(cleanLines[0])
        result.push(fixTableRowLine(headerParsed.cells, colCount))

        result.push('|' + ' :--- |'.repeat(colCount))

        const dataStart = hasSeparator ? 2 : 1
        for (let d = dataStart; d < cleanLines.length; d++) {
          const dp = parseTableRow(cleanLines[d])
          result.push(fixTableRowLine(dp.cells, colCount))
        }

        if (j < lines.length && lines[j].trim() !== '') result.push('')
        i = j
      } else {
        result.push(line)
        i++
      }
    } else {
      result.push(line)
      i++
    }
  }

  if (result.length > 0 && !inCodeBlock) {
    const last = result[result.length - 1]
    const lastTrim = last.trim()
    if (lastTrim.startsWith('|') && !lastTrim.endsWith('|')) {
      for (let k = result.length - 2; k >= 0; k--) {
        const prev = result[k]
        if (isTableLine(prev) && prev.trim().endsWith('|')) {
          const prevCols = (prev.match(/\|/g) || []).length - 1
          const currCols = (last.match(/\|/g) || []).length
          if (prevCols >= currCols) {
            result[result.length - 1] = last + ' |'.repeat(prevCols - currCols + 1)
          }
          break
        }
      }
    }
  }

  return result.join('\n')
}

let abortController = null

/**
 * 深度思考流式请求
 */
const apiBaseUrl = getApiBaseUrl() || ''
const startThinkStream = async () => {
  if (!props.keyword) return

  // 每次请求创建新的 AbortController
  abortController = new AbortController()
  const signal = abortController.signal

  const apiUrl = `${apiBaseUrl}api/DocumentSearch/CaseThink`
  
  rawThinkText.value = ''
  thinkHtml.value = ''
  let buffer = ''
  let streamRunning = true
  const token = localStorage.getItem('token')

  thinkStreaming.value = true

  try {
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        "Content-Type": "application/json",
        "Accept": "text/event-stream",
        'Authorization': 'Bearer ' + token
      },
      body: JSON.stringify({ query: props.keyword }),
      signal
    })

    // console.log('思考接口响应状态码', response.status)
    if (!response.ok || !response.body) {
      console.error('CaseThink 接口请求失败', response.status)
      thinkStreaming.value = false
      return
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder('utf-8')
    let readResult

    while (streamRunning) {
      readResult = await reader.read()
      if (readResult.done) {
        streamRunning = false
        break
      }

      const chunkStr = decoder.decode(readResult.value, { stream: true })
      buffer += chunkStr

      while (buffer.includes('\n\n')) {
        const splitIndex = buffer.indexOf('\n\n')
        const fullMsg = buffer.slice(0, splitIndex).trim()
        buffer = buffer.slice(splitIndex + 2)

        if (!fullMsg) continue

        if (fullMsg === 'data:[DONE]') {
          streamRunning = false
          break
        }

        if (fullMsg.startsWith('data:')) {
          let jsonStr = fullMsg.slice(5).trim()
          if (!jsonStr || !jsonStr.startsWith('{') || !jsonStr.endsWith('}')) {
            continue
          }

          try {
            const data = JSON.parse(jsonStr)
            if (data.content) {
              rawThinkText.value += data.content
              thinkHtml.value = marked.parse(fixStreamMarkdown(rawThinkText.value))
            }
          } catch (parseErr) {
            continue
          }
        }
      }
    }
  } catch (err) {
    if (err.name === 'AbortError') {
      console.log('CaseThink 请求已取消')
      return
    }
    console.error('CaseThink 流式数据异常：', err)
  } finally {
    thinkStreaming.value = false
    emit("finish")
  }
}

// 代码复制
const handleCopyCode = async (e) => {
  const btn = e.target
  if (!btn.classList.contains('code-copy-btn')) return
  const codeBox = btn.closest('.code-wrap')
  const codeContent = codeBox.querySelector('code').innerText
  try {
    await navigator.clipboard.writeText(codeContent)
    btn.innerText = '复制成功'
    btn.style.color = '#1768FF'
  } catch {
    btn.innerText = '复制失败'
  }
  setTimeout(() => {
    btn.innerText = '复制代码'
    btn.style.color = ''
  }, 2000)
}

// 分步计时器
const runStepTimer = () => {
  setTimeout(() => {
    currentStep.value = 1
    setTimeout(() => {
      currentStep.value = 2
    }, 4000)
  }, 3000)
}

onMounted(() => {
  document.addEventListener('click', handleCopyCode)
  runStepTimer()
  startThinkStream()
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleCopyCode)
  // 组件卸载时中断 CaseThink 请求
  if (abortController) {
    abortController.abort()
    abortController = null
  }
})

defineExpose({
  rawThinkText,
  thinkHtml,
  thinkStreaming
})
</script>

<style scoped lang="less">
.search-loading-panel {
  width: 100%;
  padding: 20px;
  box-sizing: border-box;
}

.step-content{
    height: 76vh;
    background: #FFFFFF;
    box-shadow: 0px 8px 24px 0px rgba(36,70,130,0.08);
    border-radius: 16px 16px 16px 16px;
    border: 1px solid #DFE6F2;
    padding: 29px;
    overflow-y: auto;
}

// 步骤进度标签
.step-wrap {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  
  .step-item {
    flex: 1;
    height: 48px;
    display: flex;
    align-items: center;
    padding: 16px;
    gap: 6px;
    background: #F6F8FC;
    box-shadow: 0px 8px 24px 0px rgba(36,70,130,0.08);
    border-radius: 12px 12px 12px 12px;
    border: 1px solid #EDF1F7;
    font-weight: 400;
    font-size: 13px;
    color: #66758F;
    line-height: 18px;
    transition: all 0.3s ease;
    .step-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #66758F;
      transition: all 0.3s;
    }
    .step-text {
      font-size: 13px;
      color: #86909C;
    }
    &.active {
       background: #EEF5FF;
        box-shadow: 0px 8px 24px 0px rgba(36,70,130,0.08);
        border-radius: 12px 12px 12px 12px;
        border: 1px solid #CFE0FF;
        font-weight: 400;
        font-size: 13px;
        line-height: 18px;
      .step-dot {
        background: #1768FF;
      }
      .step-text {
        color: #1768FF;
      }
    }
  }
}

// 骨架卡片容器
.skeleton-card {
  background: #fff;
  border-radius: 14px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  .skeleton-line {
    background: linear-gradient(90deg, #E8EDF5 25%, #F0F4FB 50%, #E8EDF5 75%);
    background-size: 200% 100%;
    animation: skeletonShine 1.5s infinite;
    border-radius: 6px;
  }
  .line-short {
    width: 30%;
    height: 16px;
  }
  .line-long1 {
    width: 90%;
    height: 12px;
  }
  .line-long2 {
    width: 85%;
    height: 12px;
  }
  .skeleton-block {
    width: 100%;
    height: 86px;
    background: linear-gradient(90deg, #E8EDF5 25%, #F0F4FB 50%, #E8EDF5 75%);
    background-size: 200% 100%;
    animation: skeletonShine 1.5s infinite;
    border-radius: 6px;
  }
}

// 骨架闪烁动画
@keyframes skeletonShine {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* ========== 深度思考面板：固定高度，可滚动 ========== */
.think-panel {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  margin-top: 12px;
}
.think-header {
  padding: 14px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.think-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.think-title {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
}
.dot-anim {
  animation: dotPulse 1.2s infinite;
}
@keyframes dotPulse {
  0%, 20% { opacity: 1; }
  40% { opacity: 0.4; }
  60% { opacity: 1; }
  80% { opacity: 0.4; }
  100% { opacity: 1; }
}
.think-scroll {
  max-height: 54vh;
  overflow-y: auto;
  padding: 0 20px 20px 20px;
  font-size: 14px;
  line-height: 28px;
  color: #475569;
  :deep(p) {
    margin: 8px 0;
  }
  :deep(h3) {
    font-size: 15px;
    font-weight: 600;
    margin: 16px 0 8px;
    color: #1e293b;
  }
}

/* 代码块样式 */
:deep(.code-wrap) {
  position: relative;
  margin: 14px 0;
}
:deep(.code-lang) {
  position: absolute;
  top: 10px;
  left: 14px;
  font-size: 12px;
  color: #86909c;
  z-index: 2;
}
:deep(.code-copy-btn) {
  position: absolute;
  top: 10px;
  right: 14px;
  font-size: 12px;
  color: #64748b;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 4px;
  padding: 2px 8px;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 2;
}
:deep(.code-copy-btn:hover) {
  color: #1768ff;
  background: rgba(23, 104, 255, 0.1);
}
:deep(.hljs) {
  padding: 38px 16px 16px;
  border-radius: 10px;
  font-size: 13px;
  overflow-x: auto;
  background: #f5f7fa;
}
</style>
